import { OnboardingLayout } from "@layouts/OnboardingLayout";
import { registerDeveloper } from "@services/krentDeveloper";
import { PrimaryButton } from "@shared/buttons";
import { InputComponent } from "@shared/forms";
import ImageUpload from "@shared/image-upload/ImageUpload";
import Logo from "@shared/Logo";
import {
  ErrorDisplay,
  SuccessDisplay,
  WarningDisplay,
} from "@shared/notifications";
import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import OtpModal from "./OtpModal";
import { toast } from "react-toastify";
import {
  getProfile,
  login,
  requestSignupOTP,
  verifySignupOTP,
} from "@services/krent.service";
import { addCookie } from "@utils/cookies";

interface RegistrationState {
  firstname: string;
  lastname: string;
  email: string;
  preferredName: string;
  phone: string;
  password: string;
  passwordConfirm: string;
  role: string;
  socialMediaSource: string;
  niesvNumber?: string;
  aeanNumber?: string;
}

const DeveloperUpload = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [taxNumber, setTaxNumber] = useState("");
  const [companyWebsite, setCompanyWebsite] = useState("");
  const [companyBio, setCompanyBio] = useState("");
  const [agree, setAgree] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState<{ message: string; field: string }[]>([]);
  const [companyName, setCompanyName] = useState("");
  const [companyAddress, setCompanyAddress] = useState("");
  const [loading, setLoading] = useState<boolean>(false);
  const [profilePicture, setProfilePicture] = useState<File | null>(null);
  const [showOtpModal, setShowOtpModal] = useState(false);

  const stateFromLocation = location.state as RegistrationState | null;
  const stateFromStorage = localStorage.getItem("krent-signup-developer-data");
  let state: RegistrationState | null = null;

  try {
    state =
      stateFromLocation ||
      (stateFromStorage ? JSON.parse(stateFromStorage) : null);
  } catch (error) {
    console.error("Error parsing stored developer data:", error);
    localStorage.removeItem("krent-signup-developer-data");
  }

  const { email = "", password = "" } = state || {};
  const handleImageUpload = (file: File): void => {
    setProfilePicture(file);
  };
  const handleVerifyOtp = async (otp: string) => {
    try {
      setLoading(true);
      const verifyRes = await verifySignupOTP(email, otp);
      if (!verifyRes.success) {
        toast.error(verifyRes.message || "Invalid OTP. Please try again.");
        return;
      }

      const formData = new FormData();
      Object.entries(state || {}).forEach(([key, value]) => {
        if (value) formData.append(key, value.toString());
      });
      if (taxNumber) formData.append("taxNumber", taxNumber);
      if (companyWebsite) formData.append("companyWebsite", companyWebsite);
      if (companyBio) formData.append("companyBio", companyBio);
      if (companyName) formData.append("companyName", companyName);
      if (companyAddress) formData.append("companyAddress", companyAddress);
      if (profilePicture) formData.append("photo", profilePicture);
      const registerRes = await registerDeveloper(formData);
      if (registerRes.success) {
        setSuccess("Account created successfully!");
        setShowOtpModal(false);
        localStorage.removeItem("krent-signup-developer-data");

        try {
          const loginRes = await login(email, password);
          if (loginRes?.token) {
            addCookie("1Q_SPA", loginRes.token, 24000);

            const abortController = new AbortController();
            const profileRes: any = await getProfile(abortController.signal);
            if (profileRes?.data) {
              sessionStorage.setItem("user", JSON.stringify(profileRes.data));
            }

            navigate("/developer-dashboard", {
              replace: true,
              state: { from: location, verified: true },
            });
          }
        } catch {
          toast.success("Registration successful! Please sign in.");
          navigate("/sign-in", { state: { email } });
        }
      }
    } catch (err: any) {
      toast.error(err?.message || "Error verifying OTP or creating account.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError([]);

    if (!state) {
      setError([
        {
          message: "Registration info missing. Go back and try again.",
          field: "general",
        },
      ]);
      return;
    }

    if (!agree) {
      setError([
        {
          message: "Please accept the terms and conditions to continue",
          field: "terms",
        },
      ]);
      return;
    }

    try {
      setLoading(true);
      const response = await requestSignupOTP(state.email);
      if (response.success) {
        toast.success("OTP sent to your email");
        setShowOtpModal(true);
      } else {
        toast.error(response.message || "Unable to send OTP. Try again.");
      }
    } catch (err: any) {
      toast.error(err?.message || "Something went wrong while requesting OTP");
    } finally {
      setLoading(false);
    }
  };

  return (
    <OnboardingLayout>
      <div className="container">
        <h3
          className="text-md-start fw-bolder mb-2 text-link text-black mt-3"
          onClick={() => navigate("/")}
        >
          <Logo />
        </h3>

        <h4 className="fw-bolder">Create a developer account</h4>

        <p className="text-light-black fs-14 mb-4">
          Complete your developer account setup
        </p>

        {!state && (
          <WarningDisplay
            message="We couldn't find your registration information. Please go back to complete your registration."
            onClose={() => navigate("/get-started/developer")}
          />
        )}

        <button
          type="button"
          onClick={() => navigate(-1)}
          className="btn btn-link text-decoration-none text-primary fw-bold mb-3 px-0"
        >
          ← Previous
        </button>

        <form onSubmit={handleSubmit} className={!state ? "opacity-50" : ""}>
          <div>
            <ImageUpload
              title="Enter your company image"
              onImageUpload={handleImageUpload}
            />
          </div>
          <div className="col mb-3">
            <InputComponent
              label="Company Name"
              placeholder="Enter company name"
              icon="name"
              iconType="fa"
              type="text"
              value={companyName}
              change={(e) => {
                setCompanyName(e.target.value);
              }}
            />
          </div>
          <div className="col mb-3">
            <InputComponent
              label="Company Address"
              placeholder="Enter company address"
              icon="address"
              iconType="fa"
              type="text"
              value={companyAddress}
              change={(e) => {
                setCompanyAddress(e.target.value);
              }}
            />
          </div>
          <div className="col mb-3">
            <InputComponent
              label="Company Registration Number (CRN) / CAC Number"
              placeholder="Enter number"
              icon="id-badge"
              iconType="fa"
              type="text"
              value={taxNumber}
              change={(e) => {
                setTaxNumber(e.target.value);
              }}
              required
            />
          </div>
          <div className="col mb-3">
            <InputComponent
              label="Link to company website"
              placeholder="Enter link"
              icon="link"
              iconType="fa"
              type="text"
              value={companyWebsite}
              change={(e) => {
                setCompanyWebsite(e.target.value);
              }}
            />
          </div>
          <div className="col mb-3">
            <InputComponent
              type="text"
              label="Bio(Company details)"
              placeholder="Enter your company history"
              iconType="fa"
              value={companyBio}
              change={(e) => setCompanyBio(e.target.value)}
              required
              isTextArea={true}
              rows={6}
            />
          </div>
          <div className="col mb-3">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                name="terms"
                checked={agree}
                id="terms"
                onChange={() => setAgree(!agree)}
                required
              />
              <label className="form-check-label fs-14 fw-400" htmlFor="terms">
                By signing up you agree to our{" "}
                <a href="/terms" className="text-primary fw-bold">
                  Terms of service
                </a>{" "}
                and{" "}
                <a href="/privacy" className="text-primary fw-bold">
                  Privacy policy
                </a>
              </label>
            </div>
          </div>

          <ErrorDisplay errors={error} onClose={() => setError([])} />
          <SuccessDisplay message={success} />

          <div className="d-grid">
            <PrimaryButton
              type="primary"
              text="Verify Email & Continue"
              loading={loading}
              disable={!state}
            />
          </div>
        </form>
        <p className="text-light-black text-center my-4 my-md-3 fs-14">
          Already have an account?{" "}
          <Link className="text-link" to="/sign-in">
            Sign In
          </Link>
        </p>
      </div>

      <OtpModal
        email={email}
        open={showOtpModal}
        onClose={() => setShowOtpModal(false)}
        loading={loading}
        onVerify={handleVerifyOtp}
      />
    </OnboardingLayout>
  );
};

export default DeveloperUpload;
